import { useEffect, useMemo, useRef, useState } from 'react';
import { FileSpreadsheet, Keyboard, Plus, Search, TriangleAlert, X } from 'lucide-react';
import {
  type OpeningColumnHint,
  type OpeningLineInput,
  type OpeningPreviewRow,
  type OpeningUnknownItem,
} from '@eombor/contracts';
import { Badge, Button, Dialog, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { useCreateOpening, usePreviewOpening } from '../model/opening.queries.js';
import { StoreroomPicker, type StoreroomPlace } from './StoreroomPicker.js';

const FALLBACK = "Kiritib bo'lmadi";

const errorText = (error: unknown): string =>
  error instanceof ApiClientError ? error.message : FALLBACK;

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

const dateText = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
};

/** Har harfda so'rov yubormaslik uchun — odam so'zni yozib bo'lsin. */
const SEARCH_DEBOUNCE_MS = 300;

/** `<input type="date">` uchun bugungi kun — kelasi sana tanlab bo'lmasin. */
const todayIso = (): string => new Date().toISOString().slice(0, 10);

type Mode = 'file' | 'manual';

/** Qo'lda kiritilayotgan bitta satr — hali to'liq bo'lmasligi mumkin. */
interface Draft {
  key: string;
  nomenclatureId: string | null;
  name: string;
  unit: string;
  qty: string;
  receivedAt: string;
  aktNo: string;
  note: string;
}

const emptyDraft = (): Draft => ({
  key: crypto.randomUUID(),
  nomenclatureId: null,
  name: '',
  unit: '',
  qty: '',
  receivedAt: '',
  aktNo: '',
  note: '',
});

/** Satrda NIMA yetishmayotgani — tugma o'chiq turganda shu aytiladi. */
type Need = 'Product' | 'Qty' | 'Date';

const draftNeed = (draft: Draft): Need | null => {
  if (draft.nomenclatureId === null) return 'Product';
  if (!Number.isFinite(Number(draft.qty)) || Number(draft.qty) <= 0) return 'Qty';
  if (draft.receivedAt === '') return 'Date';
  return null;
};

const draftReady = (draft: Draft): boolean => draftNeed(draft) === null;

/**
 * Bo'sh satr jim turadi: endigina qo'shilgan satrga «mahsulot tanlanmagan»
 * deb yozish — odamga hali qilmagan ishini xato qilib ko'rsatish bo'lardi.
 */
const draftTouched = (draft: Draft): boolean =>
  draft.nomenclatureId !== null ||
  draft.qty !== '' ||
  draft.receivedAt !== '' ||
  draft.aktNo !== '';

/**
 * «Kiritish» NEGA bosilmayotgani. Tugmani o'chirib qo'yib sababini
 * aytmaslik — odamni ekranga tikilib qoldirish demak: ekrandagi hamma
 * katak to'ldirilgandek ko'rinadi, lekin mahsulot ro'yxatdan TANLANMAGAN.
 */
const blockerText = (
  place: StoreroomPlace | null,
  mode: Mode,
  drafts: readonly Draft[],
  lineCount: number,
): string | null => {
  if (place === null) return t('opening.needPlace');
  if (lineCount > 0) return null;
  if (mode === 'manual') {
    for (const [index, draft] of drafts.entries()) {
      const need = draftNeed(draft);
      if (need !== null) {
        return t('opening.rowNeeds', { index: index + 1, what: t(`opening.need${need}`) });
      }
    }
  }
  return t('opening.needRows');
};

/**
 * ESKI QOLDIQNI KIRITISH — Excel fayl yoki qo'lda.
 *
 * Bo'linmada yillar davomida yig'ilgan, hali ishlatilmagan mol bor: u
 * omborning javonidan o'tmagan, talabnomasi ham yo'q, lekin bugun
 * javonda turibdi va hisobda ko'rinishi kerak.
 *
 * IKKI YO'L bitta oynada, chunki savol bitta: «nima bor va qachondan
 * beri». Katta ro'yxat Excel dan keladi (buxgalteriya baribir shunday
 * beradi), besh-o'nta narsa esa qo'lda tezroq — fayl yasash uchun
 * odamni Excel ga yuborish bekor mehnat bo'lardi.
 *
 * Fayl AVVAL faqat tekshiriladi: satrlar ekranga qaytadi va odam
 * nima yozilishini ko'rib turib tasdiqlaydi. «Yukladim va o'tib ketdi»
 * degan holat boshlang'ich qoldiq uchun juda qimmat — ikki yuz satrni
 * orqaga qaytarish hisobotlarni buzadi.
 */
export const OpeningDialog = ({ onClose }: { onClose: () => void }) => {
  const [mode, setMode] = useState<Mode>('file');
  const [place, setPlace] = useState<StoreroomPlace | null>(null);
  const [note, setNote] = useState('');

  const [drafts, setDrafts] = useState<Draft[]>([emptyDraft()]);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const preview = usePreviewOpening();
  const create = useCreateOpening();

  const previewed = preview.data ?? null;

  const lines: OpeningLineInput[] = useMemo(() => {
    if (mode === 'file') {
      return (previewed?.lines ?? []).map((row) => ({
        nomenclatureId: row.nomenclatureId,
        qty: row.qty,
        receivedAt: new Date(row.receivedAt),
        ...(row.storeroomId === null ? {} : { storeroomId: row.storeroomId }),
        ...(row.aktNo === null ? {} : { aktNo: row.aktNo }),
        ...(row.note === null ? {} : { note: row.note }),
      }));
    }
    return drafts.filter(draftReady).map((draft) => ({
      nomenclatureId: draft.nomenclatureId as string,
      qty: Number(draft.qty),
      receivedAt: new Date(`${draft.receivedAt}T00:00:00.000Z`),
      ...(draft.aktNo === '' ? {} : { aktNo: draft.aktNo }),
      ...(draft.note === '' ? {} : { note: draft.note }),
    }));
  }, [mode, previewed, drafts]);

  const ready = place !== null && lines.length > 0 && !create.isPending;
  const blocker = create.isPending ? null : blockerText(place, mode, drafts, lines.length);

  const pickFile = (file: File | undefined): void => {
    if (file === undefined) return;
    setFileName(file.name);
    preview.mutate(file);
  };

  const submit = (): void => {
    if (!ready || place === null) return;
    create.mutate(
      {
        storeroomId: place.storeroomId,
        zoneId: place.zoneId,
        note: note.trim() === '' ? null : note.trim(),
        warehouseId: null,
        lines,
      },
      { onSuccess: onClose },
    );
  };

  const error = create.isError
    ? errorText(create.error)
    : preview.isError
      ? errorText(preview.error)
      : undefined;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('opening.title')}
      description={t('opening.hint')}
      busy={create.isPending}
      onSubmit={submit}
      {...(error === undefined ? {} : { error })}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <span className="mr-auto flex min-w-0 flex-col">
            <span className="text-sm text-text-muted">
              {t('opening.readyCount', { count: lines.length })}
            </span>
            {blocker === null ? null : <span className="text-xs text-warn">{blocker}</span>}
          </span>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('opening.cancel')}
          </Button>
          <Button onClick={submit} disabled={!ready} loading={create.isPending}>
            {t('opening.submit')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Joy TEPADA: u ikkala yo'l uchun ham umumiy va faylda ombor
            ustuni bo'lmagan satrlar aynan shu yerga tushadi. */}
        <StoreroomPicker
          value={place}
          onChange={setPlace}
          title={t('opening.placeTitle')}
          hint={t('opening.placeHint')}
        />

        <div role="radiogroup" aria-label={t('opening.modeLabel')} className="flex gap-1">
          {(
            [
              { id: 'file' as const, label: t('opening.modeFile'), icon: FileSpreadsheet },
              { id: 'manual' as const, label: t('opening.modeManual'), icon: Keyboard },
            ]
          ).map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={mode === option.id}
              onClick={() => setMode(option.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium',
                'transition-colors duration-[var(--motion-fast)]',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
                mode === option.id
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border bg-surface text-text-muted hover:text-text',
              )}
            >
              <option.icon size={14} aria-hidden />
              {option.label}
            </button>
          ))}
        </div>

        {mode === 'file' ? (
          <FileMode
            fileName={fileName}
            pending={preview.isPending}
            preview={previewed}
            inputRef={fileRef}
            onPick={pickFile}
          />
        ) : (
          <ManualMode drafts={drafts} onChange={setDrafts} />
        )}

        <Input
          label={t('opening.note')}
          placeholder={t('opening.notePlaceholder')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Dialog>
  );
};

/**
 * EXCEL YO'LI — yuklash va tekshiruv natijasi.
 *
 * SHABLON HAVOLASI YO'Q va bu ataylab. U bo'sh sarlavha satridan iborat
 * edi va odamni «avval shablonni yuklab ol, 1C hisobotingni unga
 * ko'chirib yoz» degan ishga yo'llardi — ya'ni ishning bir qismini
 * odamga qaytarib berardi. Fayl esa shundoq ham tekshiriladi va xato
 * qaysi qatorda ekani aniq aytiladi.
 */
const FileMode = ({
  fileName,
  pending,
  preview,
  inputRef,
  onPick,
}: {
  fileName: string | null;
  pending: boolean;
  preview: {
    lines: OpeningPreviewRow[];
    errors: { row: number; message: string }[];
    errorTotal: number;
    unknownItems: OpeningUnknownItem[];
    columns: OpeningColumnHint[];
  } | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (file: File | undefined) => void;
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        className="sr-only"
        onChange={(event) => onPick(event.target.files?.[0])}
      />
      <Button variant="secondary" onClick={() => inputRef.current?.click()}>
        <FileSpreadsheet size={16} aria-hidden />
        {t('opening.pickFile')}
      </Button>
      {fileName === null ? null : (
        <span className="min-w-0 flex-1 truncate text-sm text-text-muted">{fileName}</span>
      )}
    </div>

    {pending ? <Skeleton className="h-32 w-full" /> : null}

    {preview === null || pending ? null : (
      <>
        {/*
          FAYL QANDAY O'QILGANI — xatolardan OLDIN.

          Yuklash xatosining eng ko'p uchragan sababi ustunning boshqa
          tushunilgani: faylda «Сумма» ham, «Количество» ham bor va
          qaysi biri miqdor bo'lgani javobda ko'rinmasdi. Odam esa 162
          satrlik xato ro'yxatidan nimani tuzatishni taxmin qilardi.
          Moslikni ko'rsatish shu taxminni olib tashlaydi.
        */}
        {preview.columns.length === 0 ? null : (
          <div className="flex flex-col gap-1 rounded-[var(--radius-md)] border border-border bg-surface-muted p-3">
            <p className="text-sm font-medium text-text">{t('opening.detected')}</p>
            <ul className="flex flex-col gap-0.5 text-xs text-text-muted">
              {preview.columns.map((column) => (
                <li key={column.field}>
                  {t(`opening.field${column.field.charAt(0).toUpperCase()}${column.field.slice(1)}`)}
                  {' ← '}
                  <span className="font-mono text-text">«{column.label}»</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-text-muted">{t('opening.detectedHint')}</p>
          </div>
        )}

        {/*
          KATALOGDA YO'Q mahsulotlar — satr bo'yicha emas, MAHSULOT
          bo'yicha. Bitta «Bolt M12» o'n satrda uchrasa, xatolar
          ro'yxati o'nta bir xil qator bilan to'lib ketardi va odam
          aslida nechta narsa yetishmayotganini ko'rmasdi.
        */}
        {preview.unknownItems.length === 0 ? null : (
          <div className="flex flex-col gap-1.5 rounded-[var(--radius-md)] border border-warn bg-warn-soft p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-warn">
              <TriangleAlert size={14} aria-hidden />
              {t('opening.unknownTitle', { count: preview.unknownItems.length })}
            </p>
            <ul className="flex max-h-32 flex-col gap-0.5 overflow-y-auto text-xs text-text">
              {preview.unknownItems.map((item) => (
                <li key={item.raw}>
                  «{item.raw}» — {t('opening.unknownRows', { count: item.rowCount, row: item.sheetRow })}
                </li>
              ))}
            </ul>
            <p className="text-xs text-text-muted">{t('opening.unknownHint')}</p>
          </div>
        )}

        {preview.errors.length === 0 ? null : (
          <div className="flex flex-col gap-1.5 rounded-[var(--radius-md)] border border-danger bg-danger-soft p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-danger">
              <TriangleAlert size={14} aria-hidden />
              {t('opening.errorCount', { count: preview.errorTotal })}
            </p>
            {/* Satr raqami Excel dagi bilan BIR XIL — odam faylda aynan
                shu qatorni topadi. */}
            <ul className="flex max-h-32 flex-col gap-0.5 overflow-y-auto text-xs text-text">
              {preview.errors.map((error) => (
                <li key={`${error.row}-${error.message}`}>
                  <span className="font-mono">{error.row}</span> — {error.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {preview.lines.length === 0 ? (
          <EmptyState
            icon={<FileSpreadsheet size={20} aria-hidden />}
            title={t('opening.emptyFile')}
            description={t('opening.emptyFileHint')}
          />
        ) : (
          <ul className="flex max-h-64 flex-col divide-y divide-border overflow-y-auto rounded-[var(--radius-md)] border border-border">
            {preview.lines.map((row) => (
              <li key={row.row} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <span className="font-mono text-xs text-text-muted">{row.code}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-text">
                  {row.nomenclatureName}
                </span>
                {row.storeroomName === null ? null : (
                  <Badge tone="neutral">{row.storeroomName}</Badge>
                )}
                <span className="tabular text-sm text-text-muted">{dateText(row.receivedAt)}</span>
                <span className="tabular text-sm font-semibold text-text">
                  {qtyText(row.qty)} {row.unit}
                </span>
              </li>
            ))}
          </ul>
        )}
      </>
    )}
  </div>
);


/** QO'LDA YO'LI — besh-o'nta narsa uchun Excel yasash bekor mehnat. */
const ManualMode = ({
  drafts,
  onChange,
}: {
  drafts: Draft[];
  onChange: (next: Draft[]) => void;
}) => {
  const patch = (key: string, next: Partial<Draft>): void =>
    onChange(drafts.map((draft) => (draft.key === key ? { ...draft, ...next } : draft)));

  return (
    <div className="flex flex-col gap-3">
      {drafts.map((draft, index) => {
        const need = draftTouched(draft) ? draftNeed(draft) : null;
        return (
        <div
          key={draft.key}
          className={cn(
            'flex flex-col gap-2 rounded-[var(--radius-md)] border bg-surface-2 p-3',
            need === null ? 'border-border' : 'border-warn/30',
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-text-muted">{index + 1}</span>
            <ProductPicker
              value={draft.nomenclatureId === null ? null : { name: draft.name, unit: draft.unit }}
              onPick={(picked) =>
                patch(draft.key, {
                  nomenclatureId: picked?.id ?? null,
                  name: picked?.name ?? '',
                  unit: picked?.unit ?? '',
                })
              }
            />
            {drafts.length === 1 ? null : (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('opening.removeRow', { index: index + 1 })}
                onClick={() => onChange(drafts.filter((row) => row.key !== draft.key))}
              >
                <X size={14} aria-hidden />
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Input
              label={`${t('opening.qty')}${draft.unit === '' ? '' : `, ${draft.unit}`}`}
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              className="w-32"
              value={draft.qty}
              onChange={(event) => patch(draft.key, { qty: event.target.value })}
            />
            {/* Sana MAJBURIY va kelasi kun tanlanmaydi: boshlang'ich
                qoldiq o'tmishdan keladi. */}
            <Input
              label={t('opening.date')}
              type="date"
              max={todayIso()}
              className="w-44"
              value={draft.receivedAt}
              onChange={(event) => patch(draft.key, { receivedAt: event.target.value })}
            />
            <Input
              label={t('opening.aktNo')}
              placeholder={t('opening.aktNoPlaceholder')}
              className="w-40"
              value={draft.aktNo}
              onChange={(event) => patch(draft.key, { aktNo: event.target.value })}
            />
          </div>

          {need === null ? null : (
            <p className="flex items-center gap-1.5 text-xs text-warn">
              <TriangleAlert size={12} aria-hidden />
              {t(`opening.need${need}`)}
            </p>
          )}
        </div>
        );
      })}

      <Button variant="secondary" size="sm" onClick={() => onChange([...drafts, emptyDraft()])}>
        <Plus size={14} aria-hidden />
        {t('opening.addRow')}
      </Button>
    </div>
  );
};

/** Katalogdan mahsulot — qidirib tanlanadi, tanlangach nomi qoladi. */
const ProductPicker = ({
  value,
  onPick,
}: {
  value: { name: string; unit: string } | null;
  onPick: (picked: { id: string; name: string; unit: string } | null) => void;
}) => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const found = useNomenclatures({ ...(debounced === '' ? {} : { search: debounced }), limit: 10 });
  const items = found.data?.items ?? [];

  if (value !== null) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">{value.name}</span>
        <Button variant="ghost" size="sm" onClick={() => onPick(null)}>
          {t('opening.changeProduct')}
        </Button>
      </span>
    );
  }

  return (
    <span className="relative min-w-0 flex-1">
      <Input
        aria-label={t('opening.product')}
        placeholder={t('opening.productPlaceholder')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        prefix={<Search size={14} aria-hidden />}
      />
      {debounced === '' ? null : found.isPending ? (
        <p className="mt-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2 py-1.5 text-xs text-text-muted">
          {t('opening.searching')}
        </p>
      ) : found.isError ? (
        /* «Topilmadi» deyish YOLG'ON bo'lardi: katalog umuman o'qilmadi. */
        <p className="mt-1 rounded-[var(--radius-sm)] border border-danger bg-danger-soft px-2 py-1.5 text-xs text-danger">
          {t('opening.searchFailed')}
        </p>
      ) : items.length === 0 ? (
        /* AVVAL bu yerda bo'm-bo'sh quti chiqardi: odam yozgan nomi
           katalogda yo'qligini bilmay, «Kiritish» nega o'chiq ekan deb
           turardi. Endi topilmagani ham, nima qilish kerakligi ham yozilgan. */
        <p className="mt-1 flex flex-col gap-0.5 rounded-[var(--radius-sm)] border border-warn/30 bg-warn-soft px-2 py-1.5">
          <span className="text-xs font-medium text-warn">
            {t('opening.notFound', { query: debounced })}
          </span>
          <span className="text-xs text-text-muted">{t('opening.notFoundHint')}</span>
        </p>
      ) : (
        <ul className="mt-1 flex max-h-40 flex-col gap-0.5 overflow-y-auto rounded-[var(--radius-sm)] border border-border bg-surface p-1">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => {
                  onPick({ id: item.id, name: item.name, unit: item.unit });
                  setQuery('');
                }}
                className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left hover:bg-accent-soft"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-text">{item.name}</span>
                <span className="font-mono text-xs text-text-muted">{item.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};
